import React from 'react';
import { NavLink } from 'react-router';
import { NAV_ICONS } from './Icons';
import { useDashboardContext } from '../context/DashboardContext';
import { getNavItems } from '../config/navItems';
import { useTranslation } from '../../../shared/i18n';
import { useWalletNotificationCount } from '../hooks/useWalletNotificationCount';

const WALLET_NAV_KEYS = new Set(['wallet', 'family_currencies']);

const WalletBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span
      className="badge bg-danger rounded-pill ms-auto"
      data-testid="wallet-notification-badge"
    >
      {count}
    </span>
  );
};

/**
 * Navigation item component - handles both React routes and external links
 */
const NavItem = ({ item, navLinkClass, navLinkStyle, onItemClick, walletCount }) => {
  const { t } = useTranslation();
  const IconComponent = NAV_ICONS[item.key];
  const isWalletItem = WALLET_NAV_KEYS.has(item.key);

  // For React routes, use NavLink
  if (item.isReactRoute !== false) {
    return (
      <NavLink
        key={item.key}
        to={item.path}
        className={navLinkClass}
        style={navLinkStyle}
        end={item.key === 'dashboard'}
        onClick={onItemClick}
      >
        {({ isActive }) => (
          <div className="d-flex align-items-center gap-3">
            {IconComponent && <IconComponent active={isActive} />}
            <span className={isActive ? 'text-white fw-medium' : 'text-secondary fw-medium'}>
              {t(item.translationKey)}
            </span>
            {isWalletItem && <WalletBadge count={walletCount} />}
          </div>
        )}
      </NavLink>
    );
  }

  // For external links (Rails routes), use regular anchor tag
  return (
    <a
      key={item.key}
      href={item.path}
      className="rounded-2 mb-2 p-3 d-block text-decoration-none"
      onClick={onItemClick}
    >
      <div className="d-flex align-items-center gap-3">
        {IconComponent && <IconComponent active={false} />}
        <span className="text-secondary fw-medium">{t(item.translationKey)}</span>
        {isWalletItem && <WalletBadge count={walletCount} />}
      </div>
    </a>
  );
};

/**
 * Navigation items component
 * Renders the appropriate navigation items based on dashboard mode
 */
export const NavigationItems = ({ navLinkClass, navLinkStyle, onItemClick }) => {
  const { mode, isKid } = useDashboardContext();
  const navItems = getNavItems(mode);

  const apiUrl = isKid
    ? '/api/children/wallet_notifications'
    : '/api/guardians/wallet_notifications';

  const { count: walletCount } = useWalletNotificationCount({ apiUrl });

  return (
    <>
      {navItems.map((item) => (
        <NavItem
          key={item.key}
          item={item}
          navLinkClass={navLinkClass}
          navLinkStyle={navLinkStyle}
          onItemClick={onItemClick}
          walletCount={walletCount}
        />
      ))}
    </>
  );
};

export default NavigationItems;
